import classNames from 'classnames'
import { useRef } from 'react'
import type { KeyboardEvent, ReactNode } from 'react'

import styles from 'features/TeamChat/teamChat.module.scss'

export type TeamTab<K extends string> = {
  key: K
  label: string
  count: number
  alert?: boolean
  panel: ReactNode
}
type Props<K extends string> = { tabs: TeamTab<K>[]; active: K; onChange: (key: K) => void }

// Where each key moves focus, from the current index; Home and End jump to the ends.
const KEY_TARGET: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (i, n) => (i + 1) % n,
  ArrowLeft: (i, n) => (i - 1 + n) % n,
  Home: () => 0,
  End: (_, n) => n - 1,
}

// A tablist of our own: the app's global antd Tabs styles force a different underline with !important.
export const TeamTabs = <K extends string>({ tabs, active, onChange }: Props<K>) => {
  const refs = useRef<(HTMLButtonElement | null)[]>([])
  const index = Math.max(
    0,
    tabs.findIndex((t) => t.key === active),
  )
  const current = tabs[index]

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    const target = KEY_TARGET[e.key]
    if (!target) return
    e.preventDefault()
    const next = target(index, tabs.length)
    onChange(tabs[next].key)
    refs.current[next]?.focus()
  }

  return (
    <>
      <div className={styles.tabBar} role='tablist'>
        {tabs.map((t, i) => (
          <button
            key={t.key}
            ref={(el) => {
              refs.current[i] = el
            }}
            id={`team-tab-${t.key}`}
            type='button'
            role='tab'
            aria-selected={t.key === active}
            aria-controls={t.key === active ? `team-panel-${t.key}` : undefined}
            tabIndex={t.key === active ? 0 : -1}
            className={classNames(styles.tab, { [styles.tabOn]: t.key === active })}
            onKeyDown={onKeyDown}
            onClick={() => onChange(t.key)}>
            <span>{t.label}</span>
            {t.count > 0 && (
              <span
                key={t.count}
                className={classNames(styles.count, { [styles.countAlert]: t.alert })}>
                {t.count}
              </span>
            )}
          </button>
        ))}
        <span
          className={styles.tabInk}
          aria-hidden='true'
          style={{ width: `${100 / tabs.length}%`, transform: `translateX(${index * 100}%)` }}
        />
      </div>
      <div
        key={current.key}
        id={`team-panel-${current.key}`}
        role='tabpanel'
        aria-labelledby={`team-tab-${current.key}`}
        className={classNames(styles.body, styles.fadeIn)}>
        {current.panel}
      </div>
    </>
  )
}
